import { z } from "zod";
// ============ Widget Type Enum ============
export const widgetTypeSchema = z.enum([
    // KPI / Stats
    "kpi-card",
    "stat-card",
    "metric-card",
    // Charts
    "line-chart",
    "area-chart",
    "bar-chart",
    "stacked-bar-chart",
    "pie-chart",
    "donut-chart",
    "scatter-chart",
    // Data Display
    "table",
    // Layout
    "section-heading",
]);
// ============ Data Source Configuration ============
export const dataSourceTypeSchema = z.enum(["parquet", "s3_tables", "tally", "google_sheet"]);
export const parquetDataSourceSchema = z.object({
    type: z.literal("parquet"),
});
export const s3TablesDataSourceSchema = z.object({
    type: z.literal("s3_tables"),
    catalog: z.string(),
    database: z.string(),
});
export const tallyDataSourceSchema = z.object({
    type: z.literal("tally"),
});
export const googleSheetDataSourceSchema = z.object({
    type: z.literal("google_sheet"),
    spreadsheetId: z.string(),
    sheetName: z.string(),
});
export const dataSourceConfigSchema = z.discriminatedUnion("type", [
    parquetDataSourceSchema,
    s3TablesDataSourceSchema,
    tallyDataSourceSchema,
    googleSheetDataSourceSchema,
]);
// ============ Google Sheet Extraction Configuration ============
// A row reference: a fixed 1-based row number, or a row label resolved against
// the live sheet at query time (survives client row inserts/deletes).
export const sheetRowRefSchema = z.union([z.number(), z.object({ label: z.string() })]);
export const sheetTableItemSchema = z.object({
    key: z.string(),
    label: z.string(),
    rows: z.array(sheetRowRefSchema),
    absolute: z.boolean().optional(),
});
export const sheetQueryConfigSchema = z.object({
    mode: z.enum(["series", "cell", "ratio", "table", "range"]),
    autoLayout: z.boolean().optional(),
    periodColumns: z.array(z.string()).optional(),
    periodLabelRow: z.number().optional(),
    periodLabelEndRow: z.number().optional(),
    periodTypeRow: z.number().optional(),
    valueRows: z.array(sheetRowRefSchema).optional(),
    absolute: z.boolean().optional(),
    onlyPeriodType: z.enum(["Actual", "Expected"]).optional(),
    cellRow: sheetRowRefSchema.optional(),
    cellColumn: z.string().optional(),
    numeratorRow: sheetRowRefSchema.optional(),
    denominatorRow: sheetRowRefSchema.optional(),
    multiplier: z.number().optional(),
    items: z.array(sheetTableItemSchema).optional(),
    // range mode: verbatim block copy (one table row per sheet row).
    blockColumns: z.array(z.string()).optional(),
    startRow: sheetRowRefSchema.optional(),
    endRow: sheetRowRefSchema.optional(),
    skipBlankRows: z.boolean().optional(),
});
// ============ Query Configuration ============
export const queryConfigSchema = z.object({
    queryTemplate: z.string(),
    queryType: z.enum(["sql", "sheet"]),
    dataSource: dataSourceConfigSchema,
    limit: z.number().optional(),
    sheet: sheetQueryConfigSchema.optional(),
});
// ============ Format Configuration ============
export const valueFormatSchema = z.object({
    type: z.enum(["number", "currency", "percent", "date", "duration", "custom", "text"]),
    decimals: z.number().optional(),
    // When true, `decimals` is treated as a MAXIMUM rather than a fixed count:
    // trailing zeros are dropped (e.g. ₹2.4Cr but ₹23Cr, not ₹23.0Cr).
    decimalsOptional: z.boolean().optional(),
    thousandsSeparator: z.boolean().optional(),
    compact: z.boolean().optional(),
    currency: z.string().optional(),
    currencyDisplay: z.enum(["symbol", "code", "name"]).optional(),
    // BCP-47 locale for digit grouping (e.g. "en-IN" for lakh/crore). Applies to
    // number/percent/default branches; currency already infers en-IN for INR.
    locale: z.string().optional(),
    dateFormat: z.string().optional(),
});
export const unitConfigSchema = z.object({
    label: z.string(),
    position: z.enum(["prefix", "suffix"]),
    spacing: z.boolean().optional(),
});
export const emptyValueConfigSchema = z.object({
    display: z.string(),
    style: z.enum(["muted", "dash", "hidden"]).optional(),
});
export const formatConfigSchema = z.object({
    valueFormat: valueFormatSchema,
    unit: unitConfigSchema.optional(),
    emptyValue: emptyValueConfigSchema.optional(),
});
// ============ KPI Card Options ============
export const kpiCardOptionsSchema = z.object({
    icon: z
        .object({
        name: z.string(),
        color: z.string().optional(),
    })
        .optional(),
    sparkline: z
        .object({
        enabled: z.boolean(),
        type: z.enum(["line", "area", "bar"]).default("line"),
        color: z.string().optional(),
        height: z.number().optional(),
    })
        .optional(),
    // Alert threshold: the value turns orange below `below` (or above `above`)
    alert: z.object({ below: z.number().optional(), above: z.number().optional() }).optional(),
});
// ============ KPI Display Configuration ============
export const kpiDisplayConfigSchema = z.object({
    format: formatConfigSchema,
    options: kpiCardOptionsSchema,
});
// ============ Chart Widget Configuration (area-chart, line-chart) ============
// Header metric item for summary section
export const headerMetricItemSchema = z.object({
    label: z.string(),
    sublabel: z.string().optional(),
    seriesId: z.string(),
    aggregation: z.enum(["sum", "avg", "min", "max", "last", "first", "count"]),
    format: valueFormatSchema,
});
// Header metrics configuration (top summary component)
export const headerMetricsConfigSchema = z.object({
    primary: headerMetricItemSchema.optional(),
    secondary: headerMetricItemSchema.optional(),
});
// Series tooltip configuration
export const seriesTooltipConfigSchema = z.object({
    enabled: z.boolean().optional(),
    valueFormat: valueFormatSchema.optional(),
    labelOverride: z.string().optional(),
});
// Chart series configuration
export const chartSeriesConfigSchema = z.object({
    id: z.string(),
    name: z.string(),
    query: queryConfigSchema,
    chartType: z.enum(["line", "area", "bar", "stacked-bar"]),
    color: z.string().optional(),
    fillOpacity: z.number().optional(),
    strokeWidth: z.number().optional(),
    lineStyle: z.enum(["solid", "dashed", "dotted"]).optional(),
    tooltip: seriesTooltipConfigSchema.optional(),
    // Invisible stacked base (waterfall): transparent, no legend or tooltip entry
    spacer: z.boolean().optional(),
});
// Chart axis configuration
export const chartAxisConfigSchema = z.object({
    label: z.string().optional(),
    dateFormat: z.string().optional(),
    format: valueFormatSchema.optional(),
    // Y axis range. Omit for the default: line charts fit their data, other charts start at zero.
    min: z.number().optional(),
    max: z.number().optional(),
    tickInterval: z.number().positive().optional(),
});
// Chart tooltip configuration
export const chartTooltipConfigSchema = z.object({
    enabled: z.boolean().optional(),
    shared: z.boolean().optional(),
    dateFormat: z.string().optional(),
});
// Chart legend configuration
export const chartLegendConfigSchema = z.object({
    enabled: z.boolean().optional(),
    position: z.enum(["top", "bottom", "left", "right"]).optional(),
});
// Chart reference line configuration (e.g. a "today" marker on a time axis).
export const chartReferenceLineConfigSchema = z.object({
    // "today" resolves client-side to the x-category nearest the current date;
    // any other string is used as the literal x-axis value.
    xValue: z.string(),
    label: z.string().optional(),
    color: z.string().optional(),
    strokeDasharray: z.string().optional(),
});
// Chart options configuration
export const chartOptionsConfigSchema = z.object({
    xAxis: chartAxisConfigSchema.optional(),
    yAxis: chartAxisConfigSchema.optional(),
    tooltip: chartTooltipConfigSchema.optional(),
    legend: chartLegendConfigSchema.optional(),
    referenceLine: chartReferenceLineConfigSchema.optional(),
});
// ============ Display Configuration (legacy, for backward compat) ============
export const displayConfigSchema = z.object({
    format: formatConfigSchema,
    options: kpiCardOptionsSchema,
});
// ============ Layout Configuration ============
export const layoutPositionSchema = z.object({
    x: z.number(),
    y: z.number(),
    w: z.number(),
    h: z.number(),
    visible: z.boolean().optional(),
});
export const responsiveLayoutConfigSchema = z.object({
    breakpoints: z
        .object({
        lg: layoutPositionSchema.optional(),
        md: layoutPositionSchema.optional(),
        sm: layoutPositionSchema.optional(),
        xs: layoutPositionSchema.optional(),
        xxs: layoutPositionSchema.optional(),
    })
        .optional(),
    default: layoutPositionSchema,
    // Phone Home only: hide the widget there, or order it explicitly (lower first).
    // The Dashboards tab and desktop ignore this and keep the full grid.
    mobile: z.object({ hidden: z.boolean().optional(), order: z.number().optional() }).optional(),
    static: z.boolean().optional(),
    minW: z.number().optional(),
    minH: z.number().optional(),
    maxW: z.number().optional(),
    maxH: z.number().optional(),
});
// ============ Widget Metadata (User Query & SQL Explanation) ============
export const widgetMetadataSchema = z.object({
    userQuery: z.string().optional(),
    sqlExplanation: z.string().optional(),
});
// ============ KPI Widget Configuration ============
export const kpiWidgetTypeSchema = z.enum(["kpi-card", "stat-card", "metric-card"]);
export const kpiWidgetConfigSchema = z.object({
    id: z.string(),
    title: z.string(),
    subtitle: z.string().optional(),
    description: z.string().optional(),
    type: kpiWidgetTypeSchema,
    query: queryConfigSchema,
    display: kpiDisplayConfigSchema,
    layout: responsiveLayoutConfigSchema,
    managedBy: z.string().optional(),
    anchor: z.string().optional(),
    userQuery: z.string().optional(),
    sqlExplanation: z.string().optional(),
});
// ============ Chart Widget Configuration ============
// A one-line takeaway shown under a widget's title: one panel's text from a materialized dataset.
export const widgetTakeawayRefSchema = z.object({
    datasetId: z.string(),
    panelId: z.string(),
});
export const chartWidgetTypeSchema = z.enum(["area-chart", "line-chart", "bar-chart", "stacked-bar-chart", "donut-chart", "scatter-chart"]);
export const chartWidgetConfigSchema = z.object({
    id: z.string(),
    title: z.string(),
    subtitle: z.string().optional(),
    description: z.string().optional(),
    type: chartWidgetTypeSchema,
    headerMetrics: headerMetricsConfigSchema.optional(),
    series: z.array(chartSeriesConfigSchema).min(1),
    chartOptions: chartOptionsConfigSchema.optional(),
    layout: responsiveLayoutConfigSchema,
    managedBy: z.string().optional(),
    takeaway: widgetTakeawayRefSchema.optional(),
    footnote: z.string().optional(),
    anchor: z.string().optional(),
    userQuery: z.string().optional(),
    sqlExplanation: z.string().optional(),
});
// ============ Table Widget Configuration ============
export const tableWidgetTypeSchema = z.literal("table");
export const tableColumnConfigSchema = z.object({
    key: z.string(), // Column key/field name from query result
    label: z.string().optional(), // Display label (defaults to key)
    unit: unitConfigSchema.optional(), // Unit configuration
    format: formatConfigSchema.optional(), // Value formatting
    width: z.number().optional(), // Column width
    align: z.enum(["left", "center", "right"]).optional(), // Text alignment
});
export const tableListConfigSchema = z.object({
    titleColumn: z.string(),
    detailColumn: z.string().optional(),
    valueColumn: z.string(),
    valueLabel: z.string().optional(),
    tag: z.object({ column: z.string(), label: z.string() }).optional(),
    link: z.object({ column: z.string(), links: z.record(z.string(), z.string()) }).optional(),
    initialRows: z.number().optional(),
    countLabel: z.string().optional(),
});
export const tableWidgetConfigSchema = z.object({
    id: z.string(),
    title: z.string(),
    subtitle: z.string().optional(),
    description: z.string().optional(),
    type: tableWidgetTypeSchema,
    query: queryConfigSchema,
    columns: z.array(tableColumnConfigSchema),
    layout: responsiveLayoutConfigSchema,
    managedBy: z.string().optional(),
    takeaway: widgetTakeawayRefSchema.optional(),
    footnote: z.string().optional(),
    pageSize: z.number().optional(),
    // Optional per-row styling for verbatim sheet tables, matched case-insensitively
    // as a substring of the first (label) column. `emphasizeRowLabels` bold + tint a
    // row (like a Total row); `dividerBeforeRowLabels` add a separating gap above it.
    emphasizeRowLabels: z.array(z.string()).optional(),
    dividerBeforeRowLabels: z.array(z.string()).optional(),
    // Colour these numeric columns' cells by value (one scale across them): a heatmap
    heatmap: z.object({ columns: z.array(z.string()) }).optional(),
    // Rows drawn as a list (title, detail, value; optional tag and link): e.g. Owner home alerts
    list: tableListConfigSchema.optional(),
    anchor: z.string().optional(),
    userQuery: z.string().optional(),
    sqlExplanation: z.string().optional(),
});
// ============ Section Heading Configuration ============
// A full-width label that groups the widgets below it ("Today · Moves every day").
// It has no query: it shows only its title and subtitle.
export const sectionWidgetTypeSchema = z.literal("section-heading");
export const sectionWidgetConfigSchema = z.object({
    id: z.string(),
    title: z.string(),
    subtitle: z.string().optional(),
    description: z.string().optional(),
    type: sectionWidgetTypeSchema,
    layout: responsiveLayoutConfigSchema,
    managedBy: z.string().optional(),
    anchor: z.string().optional(),
    userQuery: z.string().optional(),
    sqlExplanation: z.string().optional(),
});
// ============ Main Widget Configuration (Union) ============
export const widgetConfigSchema = z.union([
    kpiWidgetConfigSchema,
    chartWidgetConfigSchema,
    tableWidgetConfigSchema,
    sectionWidgetConfigSchema,
]);
// Sanitized KPI widget config (without query field for API responses)
export const sanitizedKpiWidgetConfigSchema = kpiWidgetConfigSchema.omit({ query: true });
// Sanitized chart series config (without query field)
export const sanitizedChartSeriesConfigSchema = chartSeriesConfigSchema.omit({ query: true });
// Sanitized chart widget config (without query fields in series)
export const sanitizedChartWidgetConfigSchema = chartWidgetConfigSchema
    .omit({ series: true })
    .extend({
    series: z.array(sanitizedChartSeriesConfigSchema).min(1),
});
// Sanitized table widget config (without query field)
export const sanitizedTableWidgetConfigSchema = tableWidgetConfigSchema.omit({ query: true });
// Sanitized widget config (without query fields for API responses); a section heading has no query
export const sanitizedWidgetConfigSchema = z.union([
    sanitizedKpiWidgetConfigSchema,
    sanitizedChartWidgetConfigSchema,
    sanitizedTableWidgetConfigSchema,
    sectionWidgetConfigSchema,
]);
// ============ Widget Entity Schema ============
export const widgetSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    dashboardId: z.string().uuid().nullable(),
    config: widgetConfigSchema,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// ============ Widget Query Request/Response ============
export const widgetQueryRequestSchema = z.object({
    widgetId: z.string().uuid(),
    dashboardId: z.string().uuid().nullable(),
    parameters: z.record(z.string(), z.any()),
    limit: z.number().optional(),
    offset: z.number().optional(),
    // For pagination: reuse cached Athena results instead of re-running query
    queryExecutionId: z.string().optional(),
});
// Single query response (for KPI widgets)
export const singleQueryResponseSchema = z.object({
    data: z.array(z.record(z.string(), z.any())),
    columns: z.array(z.string()),
    rowCount: z.number(), // Number of rows in current page
    totalCount: z.number().optional(), // Total count of all rows (for pagination)
    // For pagination: Athena query execution ID to reuse cached results
    queryExecutionId: z.string().optional(),
    // The widget's one-line takeaway, when its config asks for one and the dataset has it
    takeaway: z.string().optional(),
});
// Series query result (for multi-series chart widgets)
export const seriesQueryResultSchema = z.object({
    seriesId: z.string(),
    data: z.array(z.record(z.string(), z.any())),
    columns: z.array(z.string()),
});
// Multi-series query response (for chart widgets)
export const multiSeriesQueryResponseSchema = z.object({
    results: z.array(seriesQueryResultSchema),
    // The widget's one-line takeaway, when its config asks for one and the dataset has it
    takeaway: z.string().optional(),
});
// Widget query response (union of single and multi-series)
export const widgetQueryResponseSchema = z.union([
    singleQueryResponseSchema,
    multiSeriesQueryResponseSchema,
]);
// ============ Create Widget Input ============
export const createWidgetInputSchema = z.object({
    dashboardId: z.string().uuid().nullable(),
    config: widgetConfigSchema,
});
// ============ Update Widget Layout Input ============
export const widgetLayoutUpdateSchema = z.object({
    widgetId: z.string().uuid(),
    layout: responsiveLayoutConfigSchema,
});
export const updateWidgetsLayoutInputSchema = z.object({
    dashboardId: z.string().uuid(),
    layouts: z.array(widgetLayoutUpdateSchema),
});
export const updateWidgetsLayoutResponseSchema = z.object({
    updated: z.number(),
});
// ============ Sanitized Widget Schema (for API responses) ============
export const sanitizedWidgetSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    dashboardId: z.string().uuid().nullable(),
    config: sanitizedWidgetConfigSchema,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// ============ Widget List Response ============
export const widgetListResponseSchema = z.object({
    widgets: z.array(sanitizedWidgetSchema),
});
// ============ Type Guards ============
export function isKpiWidgetConfig(config) {
    return ["kpi-card", "stat-card", "metric-card"].includes(config.type);
}
export function isChartWidgetConfig(config) {
    return ["area-chart", "line-chart", "bar-chart", "stacked-bar-chart", "donut-chart", "scatter-chart"].includes(config.type);
}
export function isTableWidgetConfig(config) {
    return config.type === "table";
}
export function isSectionWidgetConfig(config) {
    return config.type === "section-heading";
}
export function isSanitizedKpiWidgetConfig(config) {
    return ["kpi-card", "stat-card", "metric-card"].includes(config.type);
}
export function isSanitizedChartWidgetConfig(config) {
    return ["area-chart", "line-chart", "bar-chart", "stacked-bar-chart", "donut-chart", "scatter-chart"].includes(config.type);
}
export function isSanitizedTableWidgetConfig(config) {
    return config.type === "table";
}
export function isSingleQueryResponse(response) {
    return "data" in response && !("results" in response);
}
export function isMultiSeriesQueryResponse(response) {
    return "results" in response;
}
